import { useEffect, useState, useCallback } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  FileText,
  FileStack,
  Users,
  Settings,
  Plus,
  Shield,
  MapPin,
  Package,
  BarChart3,
  FileCheck,
  Database,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface SearchResult {
  id: string;
  type: "case" | "task" | "document" | "template";
  title: string;
  subtitle?: string;
  link: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const { profile, isAdmin } = useAuth();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const search = useCallback(async (searchQuery: string) => {
    if (!profile?.organization_id || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const [casesRes, tasksRes, docsRes, templatesRes] = await Promise.all([
        supabase
          .from("cases")
          .select("id, case_number, title")
          .eq("organization_id", profile.organization_id)
          .or(`title.ilike.%${searchQuery}%,case_number.ilike.%${searchQuery}%`)
          .limit(5),
        supabase
          .from("case_tasks")
          .select("id, title, cases(case_number)")
          .eq("organization_id", profile.organization_id)
          .ilike("title", `%${searchQuery}%`)
          .limit(5),
        supabase
          .from("case_documents")
          .select("id, name, cases(case_number)")
          .eq("organization_id", profile.organization_id)
          .ilike("name", `%${searchQuery}%`)
          .limit(5),
        supabase
          .from("document_templates")
          .select("id, name, category")
          .eq("organization_id", profile.organization_id)
          .ilike("name", `%${searchQuery}%`)
          .limit(5),
      ]);

      const searchResults: SearchResult[] = [];

      casesRes.data?.forEach((c) => {
        searchResults.push({
          id: c.id,
          type: "case",
          title: c.title,
          subtitle: c.case_number,
          link: `/app/cases/${c.id}`,
        });
      });

      tasksRes.data?.forEach((t) => {
        const caseData = t.cases as { case_number?: string } | null;
        searchResults.push({
          id: t.id,
          type: "task",
          title: t.title,
          subtitle: caseData?.case_number,
          link: `/app/tasks`,
        });
      });

      docsRes.data?.forEach((d) => {
        const caseData = d.cases as { case_number?: string } | null;
        searchResults.push({
          id: d.id,
          type: "document",
          title: d.name,
          subtitle: caseData?.case_number,
          link: `/app/documents`,
        });
      });

      templatesRes.data?.forEach((t) => {
        searchResults.push({
          id: t.id,
          type: "template",
          title: t.name,
          subtitle: t.category,
          link: `/app/templates`,
        });
      });

      setResults(searchResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) search(query);
      else setResults([]);
    }, 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (callback: () => void) => {
    setOpen(false);
    setQuery("");
    callback();
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "case": return <FolderOpen className="w-4 h-4" />;
      case "task": return <CheckSquare className="w-4 h-4" />;
      case "document": return <FileText className="w-4 h-4" />;
      case "template": return <FileStack className="w-4 h-4" />;
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput 
        placeholder="Search or type a command..." 
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {isSearching ? "Searching..." : "No results found."}
        </CommandEmpty>

        {results.length > 0 && (
          <CommandGroup heading="Search Results">
            {results.map((result) => (
              <CommandItem
                key={`${result.type}-${result.id}`}
                onSelect={() => handleSelect(() => navigate(result.link))}
              >
                {getIcon(result.type)}
                <span className="ml-2">{result.title}</span>
                {result.subtitle && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {result.subtitle}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!query && (
          <>
            <CommandGroup heading="Quick Actions">
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/cases/new"))}>
                <Plus className="w-4 h-4 mr-2" />
                New Case
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/templates"))}>
                <FileStack className="w-4 h-4 mr-2" />
                Create Document
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Navigation">
              <CommandItem onSelect={() => handleSelect(() => navigate("/app"))}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/cases"))}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Cases
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/tasks"))}>
                <CheckSquare className="w-4 h-4 mr-2" />
                Tasks
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/documents"))}>
                <FileText className="w-4 h-4 mr-2" />
                Documents
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/templates"))}>
                <FileStack className="w-4 h-4 mr-2" />
                Templates
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/locations"))}>
                <MapPin className="w-4 h-4 mr-2" />
                Locations
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/properties"))}>
                <Package className="w-4 h-4 mr-2" />
                Properties
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/reports"))}>
                <BarChart3 className="w-4 h-4 mr-2" />
                Reports
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/app/rfp"))}>
                <FileCheck className="w-4 h-4 mr-2" />
                RFP Manager
              </CommandItem>
            </CommandGroup>

            {isAdmin && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Admin">
                  <CommandItem onSelect={() => handleSelect(() => navigate("/app/admin"))}>
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Settings
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect(() => navigate("/app/admin/users"))}>
                    <Users className="w-4 h-4 mr-2" />
                    User Management
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect(() => navigate("/app/security"))}>
                    <Shield className="w-4 h-4 mr-2" />
                    Security Dashboard
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect(() => navigate("/app/data-migration"))}>
                    <Database className="w-4 h-4 mr-2" />
                    Data Migration
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect(() => navigate("/app/cjis-compliance"))}>
                    <FileCheck className="w-4 h-4 mr-2" />
                    CJIS Compliance
                  </CommandItem>
                </CommandGroup>
              </>
            )}

            <CommandSeparator />

            <CommandGroup heading="Help">
              <CommandItem onSelect={() => handleSelect(() => navigate("/docs"))}>
                <HelpCircle className="w-4 h-4 mr-2" />
                Documentation
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/support"))}>
                <HelpCircle className="w-4 h-4 mr-2" />
                Support
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
